import classNames from "classnames";
import React, { Component } from "react";
import { IconFormInfo, IconFormSuccess, IconFormWarning } from "../icons";
import { Message } from "../message/message";
import { AriaProps } from "../types";
import { CSS_CLASSES } from "./form-field-css-classes";
import { FormFieldLabel } from "./form-field-label";
import { FormFieldTextLengthIndicator } from "./form-field-text-length-indicator";
import { FormFieldProps } from "./form-field.types";

/**
 * The FormField component provides a uniform way for handling various associated
 * UI elements typically rendered along with a specific form control/input.
 *
 * It handles the styling of these UI elements based on the various states in which
 * the underlying form field may be in.
 */
export class FormField extends Component<FormFieldProps> {
  static defaultProps: Partial<FormFieldProps> = {
    formFieldType: "input",
    hideLabel: false,
    required: false,
    disabled: false,
    readOnly: false,
  };

  labelId: string;
  hintId: string;
  errorId: string;
  successId: string;
  textLengthId: string;

  constructor(props: FormFieldProps) {
    super(props);

    this.labelId = `${this.props.formFieldId}-label`;
    this.hintId = `${this.props.formFieldId}-hint`;
    this.errorId = `${this.props.formFieldId}-error`;
    this.successId = `${this.props.formFieldId}-success`;
    this.textLengthId = `${this.props.formFieldId}-textLength`;
  }

  render() {
    const {
      formFieldId,
      formFieldMessageId,
      formFieldType,
      label,
      labelInfo,
      hideLabel,
      labelProps,
      secondaryAction,
      hint,
      success,
      error,
      textLength,
      focused,
      required,
      disabled,
      readOnly,
      className: classNameProp,
      children,
    } = this.props;

    const rootClassName = classNames(
      {
        [CSS_CLASSES.FORM_FIELD_ROOT]: true,
      },
      classNameProp
    );

    const topRowClassName = classNames({
      [CSS_CLASSES.FORM_FIELD_TOP_ROW]: true,
      [CSS_CLASSES.LABEL_ONLY]: !hideLabel && !secondaryAction,
      [CSS_CLASSES.SECONDARY_ACTION_ONLY]: hideLabel && secondaryAction,
    });

    const bottomRowClassName = classNames({
      [CSS_CLASSES.FORM_FIELD_BOTTOM_ROW]: true,
      [CSS_CLASSES.MESSAGE_ONLY]: (error || success || hint) && !textLength,
      [CSS_CLASSES.TEXT_LENGTH_INDICATOR_ONLY]: !(error || success || hint) && textLength,
    });

    const ariaProps: AriaProps = {
      "aria-required": formFieldType === "input" ? required : undefined,
      "aria-disabled": disabled,
      "aria-readonly": formFieldType === "input" ? readOnly : undefined,
      "aria-invalid": formFieldType === "input" ? !!error : undefined,
    };
    if (hideLabel) {
      ariaProps["aria-label"] = label;
    } else {
      ariaProps["aria-labelledby"] = this.labelId;
    }
    /**
     * @todo: [BDS-530] do not add "aria-describedby" attribute at all if not required, instead of having it as aria-describedby: undefined
     */
    ariaProps["aria-describedby"] = error ? this.errorId : success ? this.successId : hint ? this.hintId : undefined;
    if (textLength) {
      ariaProps["aria-describedby"] = ariaProps["aria-describedby"]
        ? `${ariaProps["aria-describedby"]} ${this.textLengthId}`
        : this.textLengthId;
    }

    return (
      <div className={rootClassName}>
        {(!hideLabel || secondaryAction) && (
          <div className={topRowClassName}>
            {!hideLabel && (
              <FormFieldLabel
                id={this.labelId}
                formFieldId={formFieldId}
                required={required}
                label={label}
                labelInfo={labelInfo}
                className={CSS_CLASSES.FORM_FIELD_LABEL}
                labelRequiredIndicatorClassName={CSS_CLASSES.FORM_FIELD_LABEL_REQUIRED_INDICATOR}
                labelTextClassName={CSS_CLASSES.FORM_FIELD_LABEL_TEXT}
                labelInfoClassName={CSS_CLASSES.FORM_FIELD_LABEL_INFO}
                labelProps={labelProps}
              />
            )}
            {secondaryAction}
          </div>
        )}
        {children(ariaProps)}
        {(error || success || hint || textLength) && (
          <div className={bottomRowClassName}>
            {(error || success || hint) && (
              <Message
                id={
                  formFieldMessageId
                    ? formFieldMessageId
                    : error
                    ? this.errorId
                    : success
                    ? this.successId
                    : hint
                    ? this.hintId
                    : undefined
                }
                size="small"
                inline={false}
                showStatusLabel={false}
                status={error ? "error" : success ? "success" : hint ? "info" : undefined}
                inactive={!error && !success && hint && !focused}
                icon={error ? <IconFormWarning /> : success ? <IconFormSuccess /> : hint ? <IconFormInfo /> : undefined}
                matchColor={Boolean(error)}
                className={CSS_CLASSES.FORM_FIELD_MESSAGE}
              >
                {error || success || hint}
              </Message>
            )}
            {textLength && (
              <FormFieldTextLengthIndicator
                id={this.textLengthId}
                textLength={textLength}
                className={CSS_CLASSES.FORM_FIELD_TEXT_LENGTH_INDICATOR}
              />
            )}
          </div>
        )}
      </div>
    );
  }
}
